/**
 * Naming the nodes an answer leans on — the ask channel's second rule, made
 * something the reader can check rather than something the model was asked.
 *
 * `CHAT_SYSTEM` has always told the model to separate what the graph says from
 * what it thinks, and nothing on screen showed which sentence was which: an
 * answer was one register of prose whatever it rested on. Here the model ends a
 * sentence that rests on a particular node with that node's id, the app checks
 * the id against the graph, and the surface renders it as a press back to the
 * node — so "the graph says this" is one click from being checked, and a
 * sentence with no mark reads as what it is: the model's own reading (D295).
 *
 * `parseActs`'s discipline, applied to prose. A mark is DATA — an id and
 * nothing else. The label a press shows comes from the session, never from the
 * model's words; an id the graph does not hold — invented, mistyped, or pruned
 * since — renders as nothing, which is the safe direction; and nothing here
 * changes a word of the answer, only whether a name after a sentence can be
 * pressed.
 *
 * WHAT A MARK IS NOT. It says which node a sentence is leaning on, never that
 * the node bears it out — pressing through and reading is how that is checked,
 * which is the point of offering the press. And it is not a citation in the
 * sense invariant 1 guards: it points inside this graph, at text already on
 * screen, and no source is named or admitted by it.
 *
 * And a reader never sees the id in it (D131): the surface prints the node's
 * label in its place, and the prose around it names every claim by its label,
 * as the contracts have always asked.
 *
 * Pure and engine-local, like `acts.ts`: `chat.ts` and the narrator put the
 * rule in the prompt, the surfaces render what survives validation, and the
 * promote press turns the marks back into the labels the reader saw.
 *
 * Carried from #84, whose D109 argued it and never merged.
 */

/**
 * What may sit between the brackets. Wider than `uid()`'s `n_<base36>` on
 * purpose — the demo's hand-written slugs carry hyphens and an imported
 * document can carry any string — so the class says only what an id can never
 * be (whitespace, a bracket) and the graph decides the rest. A bracketed phrase
 * with a space in it is prose, and is left exactly as written.
 */
const ID_CHAR = '[^\\s\\[\\]]'
const MARK = new RegExp(`\\[\\[(${ID_CHAR}{1,80})\\]\\]`, 'g')
/** The front half of a mark, as far as it can arrive before its end does. */
const MARK_PREFIX = new RegExp(`^\\[(?:\\[${ID_CHAR}{0,80}\\]?)?$`)

/**
 * The rule, appended where a surface sends the id table — the ask channel's
 * feed conversations and the voice. Tableless for `actsProtocol`'s reason: the
 * table changes whenever a pass lands and rides as the uncached `systemTail`,
 * so these words are the same bytes on every call.
 *
 * "Before its full stop" rather than after: both parse, and a mark inside the
 * sentence reads as belonging to it the way a footnote does.
 *
 * It says in as many words that a mark is neither a citation nor an id in the
 * prose, because the contracts it rides beside forbid writing either, and a
 * model reconciling the rules the cautious way would simply never mark
 * anything.
 */
export function refsProtocol(): string {
  return `NAMING THE NODES YOU LEAN ON
When a sentence of yours rests on a particular node — its claim, its reasoning, a result recorded on it, what a check found on it — end that sentence with the node's id in double square brackets, just before its full stop: [[<an id from the table below>]]. At most two per sentence, and only ids copied exactly from the table — never a label, never an invented id. The brackets are never a word of the sentence: it must read complete with them removed, and it still names every claim by its label in quotes. The person never sees the id: the app shows the node's label in its place, as a press to the node, so this is the one place an id may appear outside a line the app reads for itself. A mark is not a citation: it points at a node on this graph, never at a source, so the rule against writing citations does not forbid it. A sentence that is your own reading rather than something the graph holds gets no brackets, and that difference is how the person tells the two apart.`
}

/** One piece of an answer: prose as written, or a mark the graph holds. */
export type AnswerPiece = { text: string } | { ref: string }

/**
 * A live answer, cut where its display has to stop: before the front half of a
 * mark still arriving (`… storage [[n_l`), which would otherwise flash machine
 * text at the reader for a frame before its end lands. The acts filter's rule,
 * one module over — hold back exactly as far as the text could still become a
 * mark, and no further, so the prose keeps streaming token by token.
 */
export function heldBack(text: string): string {
  const at = text.lastIndexOf('[')
  if (at < 0) return text
  const start = at > 0 && text[at - 1] === '[' ? at - 1 : at
  return MARK_PREFIX.test(text.slice(start)) ? text.slice(0, start) : text
}

/**
 * Text split into its prose and its marks, the marks already checked against
 * the graph.
 *
 * A mark the graph does not hold is dropped WITH the spaces that introduced it,
 * so "… storage [[x]]." reads "… storage." rather than "… storage ." — spaces
 * and tabs only, never a newline, because the paragraph break before a mark is
 * the answer's own structure. A mark that repeats the one just before it, with
 * nothing but space between, is one mark: a model that stutters must not print
 * the same press twice (`parseActs`'s rule for a doubled chip).
 *
 * `live` holds back the front half of a mark at the end (see `heldBack`). A
 * finished answer holds nothing back: an unclosed pair there is the model's
 * text, and it is shown as written.
 */
export function splitRefs(
  text: string,
  nodes: Readonly<Record<string, unknown>>,
  live = false,
): AnswerPiece[] {
  const body = live ? heldBack(text) : text
  const out: AnswerPiece[] = []
  const pushText = (t: string) => {
    if (!t) return
    const prev = out.at(-1)
    if (prev && 'text' in prev) prev.text += t
    else out.push({ text: t })
  }
  let last = 0
  for (const m of body.matchAll(MARK)) {
    const at = m.index
    const id = m[1]!
    const between = body.slice(last, at)
    last = at + m[0].length
    if (nodes[id] === undefined) {
      pushText(between.replace(/[ \t]+$/, ''))
      continue
    }
    const prev = out.at(-1)
    if (prev && 'ref' in prev && prev.ref === id && between.trim() === '') continue
    pushText(between)
    out.push({ ref: id })
  }
  pushText(body.slice(last))
  return out
}

/**
 * Every mark an answer wrote, in order, where it sits, held by the graph or
 * not — the raw reading the marks harness (`evals/marks`) counts from, through
 * the same pattern the surface parses with, so the two cannot disagree about
 * what a mark is.
 */
export function markSpans(text: string): { id: string; start: number; end: number }[] {
  return [...text.matchAll(MARK)].map((m) => ({
    id: m[1]!,
    start: m.index,
    end: m.index + m[0].length,
  }))
}

/**
 * The marks turned back into what the reader saw — each node's label — for the
 * places an answer's text leaves the screen as text: a promote press hands it
 * to a pass as that pass's input (`buildPromoteExtra`), and a document prints a
 * narration. A pass reads the graph by label, and its contract says a
 * bracketed id is for the machine, so the label goes in quotes and in
 * parentheses, never in brackets: "… storage (“The lever”)." A mark the graph
 * no longer holds is dropped here as it is on screen.
 */
export function refsToLabels(
  text: string,
  nodes: Readonly<Record<string, { label: string } | undefined>>,
): string {
  return splitRefs(text, nodes)
    .map((p) => ('ref' in p ? `(“${nodes[p.ref]!.label}”)` : p.text))
    .join('')
}
